import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { format } from "date-fns";
import toast from 'react-hot-toast';
import { useMediaQuery, useTheme, Box, Typography, 
    CircularProgress, Tooltip, 
    FormControl, MenuItem, InputLabel, Select,  
} from '@mui/material';
import { DataGrid } from "@mui/x-data-grid";
import SortIcon from '@mui/icons-material/Sort';
import FlightIcon from '@mui/icons-material/Flight';
import 'react-circular-progressbar/dist/styles.css';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT, 
    OTP_DELAY_MINUTES, 
    DIVERT_RETURN_STATUS,
    EXCLUDED_FLIGHT_STATUS,
} from 'state/constants';
import { removeItemByValueImmutable, filterFlights } from 'state/helpers';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import StyledButton from 'components/StyledButton';
import FilterButton from 'components/FilterButton';
import PerformanceGauge from 'components/PerformanceGauge';
import FlightFilterDialog from 'components/FlightFilterDialog';
import CrewListDialog from 'components/CrewListDialog';
import StationScheduleList from './StationScheduleList';


const StationSchedule = () => {
    useAuthCheck();
    const timeMode = useSelector((state) => state.timemode);
    const theme = useTheme();
    const primaryDark = theme.palette.primary.dark;
    const textPrimaryColor = theme.palette.text.primary;
    
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
    const height = `calc(${LAYOUT_HEIGHT} - 78px)`;

    const { startDate, endDate } = useDateRangeContext();
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    
    const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
    const [filters, setFilters] = useState(null);
    const [openCrewListDialog, setOpenCrewListDialog] = useState(false);
    const [sortOption, setSortOption] = useState('none');
    const [port, setPort] = useState('ALL');

    const [flights, setFlights] = useState([]); 
    const [flight, setFlight] = useState([]);
    const [crewList, setCrewList] = useState([]);
    const [jumpList, setJumpList] = useState([]);
    
    const flightsData = useSelector((state) => state.flights);
    // Refs to access DataGrid APIs
    const arrivalGridRef = useRef(null);
    const departureGridRef = useRef(null);


    useEffect(() => {        
        const handleFlights = async () => {
            try {           
                setLoading(true);
                
                const imutated = removeItemByValueImmutable(
                    flightsData,
                    "flightStatus",
                    EXCLUDED_FLIGHT_STATUS,
                );
                const nonDivFlights = imutated.filter(flight => 
                    !DIVERT_RETURN_STATUS.includes(flight.flightStatus)
                );
                
                setFlights(nonDivFlights);
            } catch (error) {
                setError(error);
                toast.error(error?.message || "Failed to fetch flights!");
            } finally {
                setLoading(false);
            }
        };

        handleFlights();        
    }, [timeMode, startDate, endDate, flightsData]);
             
    const filteredFlights = useMemo(() => {
        return filterFlights({ flights, filters, timeMode });
    }, [flights, filters, timeMode]);        
    
    const sortedFlights = useMemo(() => {
        if (sortOption === 'none') return filteredFlights;

        const sorted = [...filteredFlights];
        sorted.sort((a, b) => {
            const aEtd = a.fullEtd ? new Date(a.fullEtd) : null;
            const bEtd = b.fullEtd ? new Date(b.fullEtd) : null;
            const aReg = a.flightReg || '';
            const bReg = b.flightReg || '';

            // sortOption === 'flightReg_fullEtd')                
            if (aReg !== bReg) return aReg.localeCompare(bReg);
            if (!aEtd && !bEtd) return 0;
            if (!aEtd) return 1;
            if (!bEtd) return -1;
            return aEtd - bEtd;
        });

        return sorted;
    }, [filteredFlights, sortOption]);

    // Split flights into arrivals and departures based on the selected port
    const arrivals = useMemo(() => {
        if (port === 'ALL') return sortedFlights;
        return sortedFlights?.filter(flight => flight.arr === port);
    }, [sortedFlights, port]);

    const departures = useMemo(() => {
        if (port === 'ALL') return sortedFlights;
        return sortedFlights?.filter(flight => flight.dep === port);
    }, [sortedFlights, port]);

    // Calculate metrics for gauges
    const punctualityArr = useMemo(() => {
        if (!arrivals.length) return 100;
        const onTimeArrivals = arrivals?.filter(flight => (flight.arrDelaysTime || 0) <= OTP_DELAY_MINUTES).length;
        return ((onTimeArrivals / arrivals.length) * 100).toFixed(1);
    }, [arrivals]);

    const punctualityDep = useMemo(() => {
        if (!departures.length) return 100;
        const onTimeDepartures = departures?.filter(flight => (flight.depDelaysTime || 0) <= OTP_DELAY_MINUTES).length;
        return ((onTimeDepartures / departures.length) * 100).toFixed(1);
    }, [departures]);

    const loadFactorArr = useMemo(() => {        
        if (arrivals.length === 0) return 0;
      
        const totalLoadFactor = arrivals.reduce((acc, cur) => acc + (cur.loadFactor || 0), 0);
        return (totalLoadFactor * 100 / arrivals.length).toFixed(1);
    }, [arrivals]);      

    const loadFactorDep = useMemo(() => {        
        if (departures.length === 0) return 0;
      
        const totalLoadFactor = departures.reduce((acc, cur) => acc + (cur.loadFactor || 0), 0);      
        return (totalLoadFactor * 100 / departures.length).toFixed(1);
    }, [departures]);      

    const handleFilterOpen = () => setIsFilterDialogOpen(true);
    const handleFilterClose = () => setIsFilterDialogOpen(false);
    
    const handleFilterApply = (filterValues) => {
        setFilters(filterValues); 
    };

    const handleSortToggle = () => {
        setSortOption((prev) => {
            if (prev === 'none') return 'flightReg_fullEtd';
            if (prev === 'flightReg_fullEtd') return 'none';
            return 'none';
        });
    };
    
    const handleCloseCrewListDialog = () => {
        setOpenCrewListDialog(false);
        setFlight([]);
        setCrewList([]);
        setJumpList([]);
    }; 
    
    const acTypeOptions = Array.isArray(flights) ? [...new Set(flights.map(flight => flight.acType))].sort() : [];
    const flightRegOptions = Array.isArray(flights) ? [...new Set(flights.map(flight => flight.flightReg))].sort() : [];
    const sectorTypeOptions = Array.isArray(flights) ? [...new Set(flights.map(flight => flight.sectorType))].sort() : [];
    const departureTypeOptions = Array.isArray(flights) ? [...new Set(flights.map(flight => flight.departureType))].sort() : [];
    const arrivalTypeOptions = Array.isArray(flights) ? [...new Set(flights.map(flight => flight.arrivalType))].sort() : [];
    const airportOptions = useMemo(() => {
        if (!Array.isArray(filteredFlights)) return [];
      
        const depPorts = filteredFlights.map(flight => flight.dep);
        const arrPorts = filteredFlights.map(flight => flight.arr);
        const combinedPorts = [...depPorts, ...arrPorts];
        
        return Array.from(new Set(combinedPorts)).sort(); // Unique & sorted
    }, [filteredFlights]);

      
    // Define delay thresholds and styles
    const DELAY_THRESHOLDS = useMemo(() => ({
        HIGH: { minutes: 60, color: '#f2292d' },
        MEDIUM: { minutes: 30, color: '#f29129' },
        LOW: { minutes: 15, color: '#d7f229' },
    }), []);
      
    // Centralized animation styles
    const BLINK_ANIMATION = useMemo(() => ({
        animation: 'blink 1s step-start 0s infinite',
        fontWeight: 'bold',
        '@media (prefers-reduced-motion: reduce)': {
            animation: 'none',
        },
    }), []);
      
    const getBlinkStyles = useCallback((delay, dateStr) => {
        if (!delay || isNaN(delay)) return {};
    
        let today = new Date();
        if (timeMode === 'UTC') {
            today = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
        } else {
            today.setHours(0, 0, 0, 0);
        }
    
        const flightDate = new Date(dateStr);
        const isTodayOrFuture = flightDate >= today;
    
        let color;
        if (delay > DELAY_THRESHOLDS.HIGH.minutes) {
            color = DELAY_THRESHOLDS.HIGH.color;
        } else if (delay > DELAY_THRESHOLDS.MEDIUM.minutes) {
            color = DELAY_THRESHOLDS.MEDIUM.color;
        } else if (delay > DELAY_THRESHOLDS.LOW.minutes) {
            color = DELAY_THRESHOLDS.LOW.color;
        } else {
            return {};
        }
    
        return {
            color,
            ...(isTodayOrFuture && BLINK_ANIMATION),
        };
    }, [timeMode, BLINK_ANIMATION, DELAY_THRESHOLDS]);
    
    const createTimeColumn = useCallback(({ field, headerName, delayField, timeMode }) => ({
        field: timeMode === 'UTC' ? field : `${field}LocalBase`,
        headerName,
        headerClassName: 'theme-column--header',
        flex: 1,
        renderCell: ({ row }) => {
            const delay = row[delayField] || 0;
            const dateStr = timeMode === 'UTC' ? row.flightDate : row.flightDateLocalBase;
        
            return (
                <FlexCenter
                    height="100%"
                    width="100%"
                    sx={{
                        '@keyframes blink': {
                            '60%': { opacity: 0.6 },
                        },
                    }}
                >
                    <Typography
                        variant="body2"
                        sx={getBlinkStyles(delay, dateStr)}
                    >
                        {row[timeMode === 'UTC' ? field : `${field}LocalBase`] || ''}
                    </Typography>
                </FlexCenter>
            );
        },
    }), [getBlinkStyles]);           

    const arrivalColumns = useMemo(() => [
        {
            field: timeMode === 'UTC' ? "flightActualDayArr" : "flightActualDayArrLocalBase",
            headerName: "Date",
            flex: 1,
            valueFormatter: (value) => {
                if (value == null) {
                  return '';
                }
                return `${format(value, "dd/MM")}`;
            },            
        },
        { field: 'flightNo', headerName: 'Flt', flex: 1 },
        { field: 'dep', headerName: 'From', flex: 1 },        
        ...(port === 'ALL' ? [{ field: 'arr', headerName: 'To', flex: 1 }] : []),
        { field: 'flightReg', headerName: 'Reg', flex: 1.3 },
        { field: 'acType', headerName: 'ACType', flex: 1 },
        { field: timeMode === 'UTC' ? 'sta' : 'staLocalBase', headerName: 'Sta', flex: 1 },
        createTimeColumn({
            field: 'eta',
            headerName: 'Eta',
            delayField: 'arrDelaysTime',
            timeMode,
        }),
        { field: timeMode === 'UTC' ? 'ata' : 'ataLocalBase', headerName: 'Ata', flex: 1 },
        { field: 'arrDelaysTime', headerName: 'Delay', align: 'right', flex: 1, renderCell: ({ value }) => value ? `${value}'` : '' },
        { field: 'noOfPax', headerName: 'Pax', flex: 1 },        
    ], [timeMode, port, createTimeColumn]);

    const departureColumns = useMemo(() => [
        {
            field: timeMode === 'UTC' ? "flightActualDayDep" : "flightActualDayDepLocalBase",
            headerName: "Date",
            flex: 1,
            valueFormatter: (value) => {
                if (value == null) {
                  return '';
                }
                return `${format(value, "dd/MM")}`;
            },            
        },
        { field: 'flightNo', headerName: 'Flt', flex: 1 },
        ...(port === 'ALL' ? [{ field: 'dep', headerName: 'From', flex: 1 }] : []),      
        { field: 'arr', headerName: 'To', flex: 1 },
        { field: 'flightReg', headerName: 'Reg', flex: 1.3 },
        { field: 'acType', headerName: 'ACType', flex: 1 },
        { field: timeMode === 'UTC' ? 'std' : 'stdLocalBase', headerName: 'Std', flex: 1 },
        createTimeColumn({
            field: 'etd',
            headerName: 'Etd',
            delayField: 'depDelaysTime',
            timeMode,
        }),
        { field: timeMode === 'UTC' ? 'atd' : 'atdLocalBase', headerName: 'Atd', flex: 1 },
        { field: 'depDelaysTime', headerName: 'Delay', align: 'right', flex: 1, renderCell: ({ value }) => value ? `${value}'` : '' },
        { field: 'noOfPax', headerName: 'Pax', flex: 1 },
        
    ], [timeMode, port, createTimeColumn]);
    
    const hiddenColumns = useMemo(() => ({
        std: !isMobile, 
        stdLocalBase: !isMobile, 
        sta: !isMobile, 
        staLocalBase: !isMobile, 
        flightReg: !isMobile, 
        acType: !isMobile, 
    }), [isMobile]);

    const nonMobileHiddenColumns = useMemo(() => ({
        flightReg: !isMobile, 
        acType: !isMobile, 
    }), [isMobile]);

    // Function to find the index of the row with fullEta/ fullEtd closest to now    
    const findClosestEtaIndex = useCallback((flights) => {
        if (!flights || flights.length === 0) return -1;

        let now = new Date();
        if (timeMode === 'UTC') {
            now = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
        } else {
            now.setHours(0, 0, 0, 0);
        }

        let closestIndex = 0;
        let smallestDiff = Infinity;

        flights.forEach((flight, index) => {
            if (!flight.fullEta) return; // Skip if fullEtd is not defined
            const eta = timeMode === 'UTC' ? new Date(flight.fullEta) : new Date(flight.fullEtaLocalBase);
            const diff = Math.abs(eta - now);

            if (diff < smallestDiff) {
                smallestDiff = diff;
                closestIndex = index;
            }
        });

        return smallestDiff !== Infinity ? closestIndex : -1;
    }, [timeMode]);

    const findClosestEtdIndex = useCallback((flights) => {
        if (!flights || flights.length === 0) return -1;

        let now = new Date();
        if (timeMode === 'UTC') {
            now = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
        } else {
            now.setHours(0, 0, 0, 0);
        }

        let closestIndex = 0;
        let smallestDiff = Infinity;

        flights.forEach((flight, index) => {
            if (!flight.fullEtd) return; // Skip if fullEtd is not defined
            const etd = timeMode === 'UTC' ? new Date(flight.fullEtd) : new Date(flight.fullEtdLocalBase);
            const diff = Math.abs(etd - now);

            if (diff < smallestDiff) {
                smallestDiff = diff;
                closestIndex = index;
            }
        });

        return smallestDiff !== Infinity ? closestIndex : -1;
    }, [timeMode]);

    // Function to scroll both DataGrids to the closest fullEta/ fullEtd
    const scrollToClosestRow = useCallback(() => {
        // Scroll Arrivals grid
        if (arrivalGridRef.current) {
            const closestArrivalIndex = findClosestEtaIndex(arrivals);
            if (closestArrivalIndex >= 0) {
                arrivalGridRef.current.scrollToIndexes({ rowIndex: closestArrivalIndex });
            }
        }

        // Scroll Departures grid
        if (departureGridRef.current) {
            const closestDepartureIndex = findClosestEtdIndex(departures);
            if (closestDepartureIndex >= 0) {
                departureGridRef.current.scrollToIndexes({ rowIndex: closestDepartureIndex });
            }
        }
    }, [arrivals, departures, findClosestEtaIndex, findClosestEtdIndex]);

    // Set up interval for auto-scrolling
    useEffect(() => {
        // Initial scroll when data loads
        if (!loading && arrivals.length > 0 && departures.length > 0) {
            scrollToClosestRow();
        }

        // Set up interval to scroll every 60 seconds
        const intervalId = setInterval(() => {
            scrollToClosestRow();
        }, 2 * 60 * 1000); // 2 minutes

        // Clean up interval on unmount
        return () => clearInterval(intervalId);
    }, [loading, arrivals, departures, scrollToClosestRow]);
    
    
    return (
        <Box className="main-container-box">                 
            <FlexBetween>
                <Header 
                    title="Station Schedule" 
                    subtitle={`${loading ? 'Loading...' : 'List of Flights'}`}
                />
                {!loading && (                    
                    <Box display="flex" alignItems="center" gap={1}>  
                        <FormControl size="small">
                            <InputLabel>Port</InputLabel>
                            <Select
                                label="Port"
                                value={port ? port : ''}
                                onChange={(e) => setPort(e.target.value)}
                                sx={{ width: isMobile ? '5rem' : '6rem' }}
                            >
                                <MenuItem key='ALL' value='ALL'>ALL</MenuItem>
                                {airportOptions?.map((airport, index) => (
                                    <MenuItem key={index} value={airport}>
                                        {airport}
                                    </MenuItem>
                                ))}                                                               
                            </Select>
                        </FormControl>

                        <Tooltip title={`Sort by ${sortOption === 'flightReg_fullEtd' ? 'Reg + Etd' : 'Etd'}`} arrow>
                            <span>
                                <StyledButton 
                                    onClick={handleSortToggle}
                                    sx={{
                                        color: sortOption === 'none' 
                                            ? `${textPrimaryColor} !important` 
                                            : `${primaryDark} !important`,
                                    }}
                                >
                                    <SortIcon />
                                </StyledButton>                                
                            </span>
                        </Tooltip>
                        
                        <FilterButton
                            filters={filters}
                            onClick={handleFilterOpen}
                        />
                    </Box>
                )}                
            </FlexBetween>          
            {loading ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <CircularProgress />
                </FlexCenter>
            ) : error ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <Typography color="error">Error: {error?.message || ''}</Typography>
                </FlexCenter>
            ) : isMobile ? (
                <Box height={LAYOUT_HEIGHT} sx={{ overflowY: 'auto' }}>
                    <StationScheduleList
                        arrivals={arrivals}
                        departures={departures}
                    />
                </Box>
            ) : (
                <Box
                    display="grid"
                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                    rowGap={isDesktop ? 5 : 15}
                    gap={2}
                >
                    <Box sx={{ gridColumn: "span 6" }}>
                        <FlexBetween mb={1}>
                            <FlexBetween color={primaryDark} gap={1}>
                                <FlightIcon/>
                                <Typography variant="h6">Arrivals</Typography>
                            </FlexBetween>
                            <FlexBetween height={68} gap={2}>
                                <PerformanceGauge
                                    title="Load Factor"
                                    indicator={loadFactorArr}
                                    pathColor="#f29129"
                                />
                                <PerformanceGauge
                                    title="Punctuality"
                                    indicator={punctualityArr}
                                    pathColor="#29f2d7"
                                />
                            </FlexBetween>
                        </FlexBetween>

                        <Box height={height}>
                            <DataGrid
                                apiRef={arrivalGridRef}
                                getRowId={(row) => row._id}
                                rows={arrivals}
                                columns={arrivalColumns}
                                pageSizeOptions={[25, 50, 100]}
                                initialState={{ pagination: { paginationModel: { pageSize: 100 } } }}
                                columnVisibilityModel={!isDesktop ? hiddenColumns : nonMobileHiddenColumns}
                            />
                        </Box>
                    </Box>

                    <Box sx={{ gridColumn: "span 6" }}>
                        <FlexBetween mb={1}>
                            <FlexBetween color={primaryDark} gap={1}>
                                <FlightIcon/>
                                <Typography variant="h6">Departures</Typography>
                            </FlexBetween>
                            <FlexBetween gap={2}>
                                <PerformanceGauge
                                    title="Load Factor"
                                    indicator={loadFactorDep}
                                    pathColor="#f29129"
                                />
                                <PerformanceGauge
                                    title="Punctuality"
                                    indicator={punctualityDep}
                                    pathColor="#29f2d7"
                                />
                            </FlexBetween>
                        </FlexBetween>

                        <Box height={height}>
                            <DataGrid
                                apiRef={departureGridRef}
                                getRowId={(row) => row._id}
                                rows={departures}
                                columns={departureColumns}
                                pageSizeOptions={[25, 50, 100]}
                                initialState={{ pagination: { paginationModel: { pageSize: 100 } } }}
                                columnVisibilityModel={!isDesktop ? hiddenColumns : nonMobileHiddenColumns}
                            />
                        </Box>
                    </Box>
                </Box>
            )}

            {isFilterDialogOpen &&
                <FlightFilterDialog
                    open={isFilterDialogOpen}
                    onClose={handleFilterClose}
                    onApply={handleFilterApply}
                    acTypeOptions={acTypeOptions} 
                    flightRegOptions={flightRegOptions}
                    sectorTypeOptions={sectorTypeOptions}
                    departureTypeOptions={departureTypeOptions}
                    arrivalTypeOptions={arrivalTypeOptions}
                />
            }
            
            {openCrewListDialog &&
                <CrewListDialog 
                    open={openCrewListDialog}
                    onClose={handleCloseCrewListDialog}
                    flight={flight}
                    crewList={crewList}
                    jumpList={jumpList}
                />
            } 
        </Box>
    );
};

export default StationSchedule;
